import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errMsg } from '../api.js';
import { Drawer, FilterBar, SegmentedControl } from '../components/ui';
import {
  daysSilent,
  isStale as recIsStale,
  STALE_DAYS,
  PIPELINE_STAGES,
  IN_PIPELINE_STATUSES,
  TERMINAL_STATUSES,
} from '@hunter/shared/applications';
import { toast } from '../components/Toasts.jsx';
import MailThread from '../components/MailThread.jsx';
import { useGlobalOverlayOpen } from '../util/globalOverlays.js';
import CoverLetterModal from '../components/CoverLetterModal.jsx';
import {
  openCompanyResearch as openCompanyResearchGlobal,
  // T422c-fix — aliased on purpose: this page already has a LOCAL
  // `openArtifact(rec, kind)` (the marker handler), and importing the same
  // name shadowed it so the handler called ITSELF with the arguments
  // swapped. No branch matched, so pressing the 🎙 marker did nothing and
  // the deep link did nothing. Lint saw only an unused import, a warning.
  openArtifact as openShellArtifact,
  useRecPatchSubscription,
} from '../util/artifactHost.js';
import TailoredCvModal from '../components/TailoredCvModal.jsx';
import ScoreBadge from '../components/ScoreBadge.jsx';
import EmptyState from '../components/EmptyState.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import DispositionModal, { dispositionLabel, statusHasDisposition } from '../components/DispositionModal.jsx';
import { formatDate, formatDateTime, timeAgo } from '../util/time.js';
import { FollowUpPanel, InterviewRetroPanel, NegotiationPrepPanel } from '../components/PipelineCoachPanels.jsx';
import { refreshNavCounts } from '../hooks/useNavCounts.js';
import { STAGES } from '../components/StatusPill.jsx';
import { Emoji, announce } from '../util/a11y.jsx';
import { bandOf } from '@hunter/shared/scoreBands';
import { reachedStage } from '@hunter/shared/applications';
import { useUrlFilters, serializeFilters, parseQuery, sameQuery } from '../hooks/useUrlFilters.js';
import { listViews, saveView, deleteView, setDefaultView, getDefaultView } from '../util/savedViews.js';
import SavedViewsBar from '../components/SavedViewsBar.jsx';
import { toCsv, downloadCsv, csvFilename } from '../util/csv.js';

// T137 — Applications pipeline sort/show state, mirrored to the URL.
// Viz-roadmap drill-down: source/company/country are set when you click a
// "What's converting" bar on /analytics — they narrow the board to that
// dimension and surface a clearable chip.
const APPLICATIONS_FILTER_SPEC = {
  // T231 — the board had sort, show and the analytics drill chips, and no way
  // to find one application by name. With 71 sent and 20 closed, the only
  // route to a specific one was scrolling four stage groups. Same `q` param and
  // the same `kind: 'text'` as the dashboard, so a search is shareable and
  // Back-able exactly like the filters beside it.
  searchQ: { param: 'q', kind: 'text', default: '' },
  sortKey: { param: 'sort', kind: 'enum', default: 'recent' },
  filterKey: { param: 'show', kind: 'enum', default: 'active' },
  source: { param: 'source', kind: 'text', default: '' },
  company: { param: 'company', kind: 'text', default: '' },
  country: { param: 'country', kind: 'text', default: '' },
  // T331 — the fourth drill target. Analytics could link here by source,
  // company and country; a score band had nowhere to land, so the band bars on
  // the calibration card were the one dimension you could read and not open.
  band: { param: 'band', kind: 'text', default: '' },
  // T334 — the fifth. "v3 advances at 40% and v2 at 12%" is the most actionable
  // line on the converting card; opening those rows is what turns it into an
  // action.
  cv: { param: 'cv', kind: 'text', default: '' },
  // T340 — the high-water mark, not the current status. "Reached interview"
  // includes the ones rejected after two of them, which is exactly what the
  // funnel bar counted.
  reached: { param: 'reached', kind: 'text', default: '' },
};
const DRILL_KEYS = ['source', 'company', 'country', 'band', 'cv', 'reached'];

// T026 — Application tracker / Kanban.
//
// Pipeline view of every recommendation the operator has marked as
// "applied" (or any post-applied sub-state). Cards drag across columns to
// advance the candidacy. Clicking a card opens a side drawer with the
// operator's notes (phone-screen times, recruiter names) + a shortcut to
// the cover letter modal.
//
// Note: shortlisted / new / dismissed do NOT appear here — the dashboard
// owns the "should I even apply to this?" decision. This page is purely
// the post-Send tracker.

// Pipeline = the value chain (applied → offer). Closed = the dead-ends.
// Splitting visually means the operator's attention lands on what's
// still in play — closed apps are reference material, not a triage zone.
// T271(1) — keys, labels and order come from the shared registry; only the
// colours are this page's business. A stage added there appears here with no
// edit, which is what stopped being true when this list was written by hand.
const TONES = {
  applied: { tone: 'border-slate-700 bg-slate-900/40', accent: 'text-slate-300' },
  screening: { tone: 'border-brand-700 bg-brand-600/10', accent: 'text-brand-400' },
  interview: { tone: 'border-amber-700 bg-amber-950/15', accent: 'text-amber-300' },
  offer: { tone: 'border-emerald-700 bg-emerald-950/15', accent: 'text-emerald-300' },
  rejected: { tone: 'border-red-800 bg-red-950/15', accent: 'text-red-400' },
  withdrawn: { tone: 'border-slate-700 bg-slate-900/30', accent: 'text-slate-500' },
};
const COLUMNS = PIPELINE_STAGES.map((st) => ({
  key: st.key,
  label: st.label,
  ...(TONES[st.key] ?? { tone: 'border-slate-700 bg-slate-900/40', accent: 'text-slate-300' }),
}));
const ACTIVE_COLUMNS = COLUMNS.filter((c) => IN_PIPELINE_STATUSES.has(c.key));
const CLOSED_COLUMNS = COLUMNS.filter((c) => TERMINAL_STATUSES.has(c.key));
const COLUMN_KEYS = COLUMNS.map((c) => c.key);
// Terminal = closes the application out of the active pipeline. Moving INTO one
// of these (from a live stage) is guarded behind the disposition modal, which
// doubles as the close-out confirm.
const TERMINAL_KEYS = TERMINAL_STATUSES;

export default function ApplicationsPage() {
  const [recs, setRecs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // T249 — a fetch failure used to land on the "No applications tracked yet"
  // empty state, which tells the operator the opposite of what happened: that
  // his pipeline is empty rather than that we could not read it. He has 44 open
  // applications.
  const [loadErr, setLoadErr] = useState(null);
  // T290 — which stages the server said it had more of. A board that silently
  // shows the first 100 of a stage reads as the whole pipeline.
  const [truncated, setTruncated] = useState([]);
  // T424 — stages whose last load failed. They keep their previous rows and
  // are labelled, rather than the board blanking.
  const [staleColumns, setStaleColumns] = useState([]);
  const globalOverlayOpen = useGlobalOverlayOpen();
  const [selectedRec, setSelectedRec] = useState(null);
  const [coverLetterRec, setCoverLetterRec] = useState(null);
  const [tailoredCvRec, setTailoredCvRec] = useState(null); // T067
  // Phase 4 — disposition modal. mode 'move' (terminal close-out, doubles as the
  // confirm) or 'edit' (drawer Outcome editor). { recId, status, newStatus?, mode, jobTitle, current? }
  const [pendingDisposition, setPendingDisposition] = useState(null);

  // T415 — `?focus=<recId>` opens that application's drawer.
  //
  // Every "Open the application" link in the product used to go to a bare
  // /applications, so the answer to "which one?" was for him to find it
  // again in a list of a hundred. Scheduled rows, mail search hits and the
  // digest all pointed here and all stopped at the door.
  //
  // Cold-load only, and the row is cleared from the URL after it opens: the
  // drawer is a place he can close, and a `focus` that survived closing
  // would reopen it on every re-render.
  //
  // T440 — and `&open=<artefact>` opens one of its tools on arrival.
  //
  // The Tasks page wants to send him from "you have an interview at Keepit"
  // straight to the prep material. It cannot mount the artefact modals to
  // do that — a third mounting is the direction T422 is trying to leave —
  // so it links here and says which tool it meant. One more parameter on a
  // deep link that already exists, rather than a new surface.
  const [pageParams] = useSearchParams();
  const focusId = pageParams.get('focus');
  const openArtifactParam = pageParams.get('open');
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current || !focusId || !recs.length) return;
    const hit = recs.find((r) => r.id === focusId);
    if (!hit) return;
    focusedRef.current = true;
    setSelectedRec(hit);
    // A closed enum, checked here rather than trusted: `open` arrives from
    // the URL, and an unknown value must do nothing rather than reach for
    // a setter that does not exist (the T410 lesson about route keys).
    if (openArtifactParam === 'interview-prep') openShellArtifact('interview-prep', hit);
    else if (openArtifactParam === 'cover-letter') setCoverLetterRec(hit);
    else if (openArtifactParam === 'tailored-cv') setTailoredCvRec(hit);
  }, [focusId, openArtifactParam, recs]);


  // T422v — open an artefact straight from the row.
  //
  // The markers ✍ 📑 🎙 showed whether each artefact existed and could not be
  // clicked, so producing a missing one meant: open the drawer, scroll to
  // the buttons, press. Three interactions to reach the tools that are the
  // most used in the product — 177 tailored CVs, 133 cover letters.
  //
  // This is the HALF of T422 that does not need the modals consolidated
  // into one shell-mounted drawer. The adversarial review of that plan
  // returned 23 blockers with line-level evidence — no GET for a single
  // recommendation, a per-page `childModalOpen` that a shell drawer is
  // invisible to (so the triage keyboard re-arms under it and `d` dismisses
  // a row), three incompatible `onUpdated` contracts — so the architecture
  // half is re-planned behind a step 0. The value half is this, and it is
  // safe: the same per-page modals, opened from one more place.
  const openArtifact = useCallback((rec, kind) => {
    if (kind === 'cover-letter') setCoverLetterRec(rec);
    else if (kind === 'tailored-cv') setTailoredCvRec(rec);
    else if (kind === 'interview-prep') openShellArtifact('interview-prep', rec);
  }, []);

  const refresh = useCallback(async () => {
    // T424 — one column failing must cost one column, not the board.
    //
    // This was `Promise.all` over six independent requests, so a single 500
    // — or one slow column timing out — rejected the whole thing, set
    // `loadErr`, and replaced the entire pipeline with an error state. Five
    // columns had arrived and were thrown away. The stakes are not
    // theoretical: this is the screen he opens to see what is live, and the
    // most valuable rows on it (`interview`, `offer`) are in the smallest,
    // most rarely-touched columns.
    //
    // `allSettled` keeps what arrived. The board then renders what it has
    // and says, by name, which stages it could not reach — which is the
    // honest version of the same information, and the one he can act on
    // ("Retry" affects only what is missing).
    //
    // Pull each column independently — the API defaults to status filter
    // when ?status= is passed, so we hit it once per column.
    const settled = await Promise.allSettled(
      COLUMN_KEYS.map((s) => api.get(`/recommendations?status=${s}&limit=100`)),
    );

    const failed = COLUMN_KEYS.filter((_, i) => settled[i].status === 'rejected');
    const ok = settled.map((r) => (r.status === 'fulfilled' ? r.value : null));

    // A column that failed keeps whatever it already had rather than
    // blinking out: a stale row is closer to the truth than an absent one,
    // and the banner says which stages are stale.
    setRecs((prev) => {
      const kept = prev.filter((r) => failed.includes(r.status));
      const fresh = ok.filter(Boolean).flatMap((r) => r.recommendations ?? []);
      return [...fresh, ...kept];
    });

    // T290 — the route returns `hasMore` and this dropped it, so a stage past
    // 100 rows would truncate in silence and the board would read as the whole
    // pipeline. Nowhere near it today — the largest stage holds 46 — but a
    // silent cut is the failure this codebase has now found in four separate
    // lists, and the cheapest moment to close it is before it bites.
    setTruncated(COLUMN_KEYS.filter((_, i) => ok[i]?.hasMore));
    setStaleColumns(failed);

    if (failed.length === COLUMN_KEYS.length) {
      // Nothing arrived at all — that IS the whole-board failure, and it
      // should still read as one.
      const msg = errMsg(settled[0].reason, 'Could not load your applications');
      setLoadErr(msg);
      toast(msg, { kind: 'error' });
    } else {
      setLoadErr(null);
      if (failed.length) {
        toast(`Could not load: ${failed.join(', ')}. The rest is up to date.`, { kind: 'error' });
      }
      refreshNavCounts();
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(COLUMN_KEYS.map((k) => [k, []]));
    for (const r of recs) {
      if (map[r.status]) map[r.status].push(r);
    }
    // T256(11) — no sort here. PipelineList re-sorts every column by the
    // operator's chosen sortKey before rendering, so a pre-sort in the grouping
    // memo was work whose result was always thrown away. Removing it also ends
    // the appearance that this file has two competing orderings.
    return map;
  }, [recs]);

  const totalApplications = recs.length;

  const moveRec = useCallback(
    // Phase 4 — `extra` optionally carries { disposition, closedReason } when the
    // move is a terminal close-out captured via the disposition modal.
    async (recId, newStatus, extra = {}) => {
      const cur = recs.find((r) => r.id === recId);
      if (!cur || cur.status === newStatus) return;
      // Optimistic update — flip the status locally, roll back if API fails.
      setRecs((prev) => prev.map((r) => (r.id === recId ? { ...r, status: newStatus, ...extra } : r)));
      try {
        const r = await api.patch(`/recommendations/${recId}`, { status: newStatus, ...extra });
        // Keep our local rec in sync with whatever the server stamped
        // (appliedAt may have been set on first transition; disposition cleared
        // on a status change and re-set from `extra`).
        const synced = {
          status: r.recommendation.status,
          appliedAt: r.recommendation.appliedAt ?? null,
          statusHistory: r.recommendation.statusHistory ?? cur.statusHistory, // T128
          disposition: r.recommendation.disposition ?? null,
          closedReason: r.recommendation.closedReason ?? '',
        };
        setRecs((prev) => prev.map((row) => (row.id === recId ? { ...row, ...synced } : row)));
        if (selectedRec?.id === recId) {
          setSelectedRec((s) => (s ? { ...s, ...synced } : s));
        }
        // T254(5) + T255(2) — a successful move used to be silent: on mobile
        // the card just vanishes from the tab you are looking at, and for a
        // screen reader the pressed "→ Next" button unmounts, dropping focus
        // to <body> with nothing said. Toast for the eye, announce for AT.
        const moved = `Moved to ${COLUMNS.find((c) => c.key === newStatus)?.label ?? newStatus}`;
        toast(moved, { kind: 'success' });
        announce(`${cur.job?.title ?? 'Application'}: ${moved}`);
      } catch (e) {
        // Roll back status + disposition.
        setRecs((prev) =>
          prev.map((row) =>
            row.id === recId ? { ...row, status: cur.status, disposition: cur.disposition ?? null, closedReason: cur.closedReason ?? '' } : row,
          ),
        );
        toast(e.body?.message || e.message, { kind: 'error' });
      }
    },
    [recs, selectedRec],
  );

  // Phase 4 — set/edit the disposition without changing status (drawer Outcome
  // editor — the only path for offer_accepted vs offer_declined).
  const saveDisposition = useCallback(async (recId, disposition, closedReason) => {
    try {
      const r = await api.patch(`/recommendations/${recId}`, { disposition: disposition ?? null, closedReason: closedReason ?? '' });
      const patch = {
        disposition: r.recommendation.disposition ?? null,
        closedReason: r.recommendation.closedReason ?? '',
      };
      setRecs((prev) => prev.map((row) => (row.id === recId ? { ...row, ...patch } : row)));
      setSelectedRec((s) => (s && s.id === recId ? { ...s, ...patch } : s));
      toast('Outcome saved', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    }
  }, []);

  // Gatekeeper in front of moveRec. Moving INTO a terminal stage from a live
  // one opens a confirm; every other move (advance, or moving back out of a
  // terminal stage) goes straight through. Both the "→ Next" button and the
  // drawer Stage <select> feed this.
  const requestMove = useCallback(
    (recId, newStatus) => {
      const cur = recs.find((r) => r.id === recId);
      if (!cur || cur.status === newStatus) return;
      if (TERMINAL_KEYS.has(newStatus) && !TERMINAL_KEYS.has(cur.status)) {
        // Phase 4 — the disposition modal IS the close-out confirm + captures why.
        setPendingDisposition({ recId, status: newStatus, newStatus, mode: 'move', jobTitle: cur.job?.title });
        return;
      }
      moveRec(recId, newStatus);
    },
    [recs, moveRec],
  );

  // Open the drawer Outcome editor for a rec already in a disposition-bearing
  // status (offer / rejected / withdrawn).
  const editOutcome = useCallback((rec) => {
    setPendingDisposition({
      recId: rec.id,
      status: rec.status,
      mode: 'edit',
      jobTitle: rec.job?.title,
      current: { disposition: rec.disposition ?? null, closedReason: rec.closedReason ?? '' },
    });
  }, []);

  const saveNotes = useCallback(async (recId, notes) => {
    try {
      await api.patch(`/recommendations/${recId}`, { applicationNotes: notes });
      setRecs((prev) => prev.map((r) => (r.id === recId ? { ...r, applicationNotes: notes } : r)));
      toast('Notes saved', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    }
  }, []);

  // T422-0 — one way to write an artefact back, and it writes to BOTH caches.
  //
  // This page holds two: `recs` (the board) and `selectedRec` (the open
  // drawer, a snapshot taken at row click). The page's own established
  // handlers — `onRecPatched` (:451) and `editOutcome` (:267) — patch both.
  // The three artefact handlers patched only `recs`, so generating a cover
  // letter from inside the drawer left the drawer's own button still
  // reading "✍ Cover letter" with no ✓ until it was closed and reopened.
  // A tool reporting that its own output does not exist.
  //
  // Found by the adversarial review of the drawer plan (T422): consolidating
  // onto "what the Applications host does" would have enshrined this.
  // Fixing it first, with both mounts still in place, is that review's other
  // recommendation — the callback contract before any tab moves.
  const patchRec = useCallback((id, patch) => {
    if (!id) return;
    setRecs((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setSelectedRec((sel) => (sel && sel.id === id ? { ...sel, ...patch } : sel));
  }, []);

  // T422b — the shell has no row cache; it publishes and this page applies.
  useRecPatchSubscription(patchRec);

  // T474 — one contract: every producer emits a PATCH whose keys are the
  // recommendation's own field names, so a host needs no per-artefact
  // knowledge and the mapping cannot drift between the two hosts. It used to
  // live here AND on the dashboard, spelled slightly differently — the
  // dashboard's interview-prep handler dropped `previous` entirely.
  //
  // T133's point survives because the producer names the field itself now:
  // `coverLetterPrevious` arrives inside the patch.
  const onCoverLetterUpdated = useCallback(
    (patch) => patchRec(coverLetterRec?.id, patch),
    [coverLetterRec, patchRec],
  );


  const onTailoredCvUpdated = useCallback(
    (patch) => patchRec(tailoredCvRec?.id, patch),
    [tailoredCvRec, patchRec],
  );

  return (
    <div className="space-y-4">
      {coverLetterRec && (
        <CoverLetterModal
          rec={coverLetterRec}
          onClose={() => setCoverLetterRec(null)}
          onUpdated={onCoverLetterUpdated}
        />
      )}
      {tailoredCvRec && (
        <TailoredCvModal
          rec={tailoredCvRec}
          onClose={() => setTailoredCvRec(null)}
          onUpdated={onTailoredCvUpdated}
        />
      )}

      {pendingDisposition && (
        <DispositionModal
          status={pendingDisposition.status}
          jobTitle={pendingDisposition.jobTitle}
          mode={pendingDisposition.mode}
          current={pendingDisposition.current}
          onCancel={() => setPendingDisposition(null)}
          onConfirm={async (disposition, closedReason) => {
            if (pendingDisposition.mode === 'edit') {
              await saveDisposition(pendingDisposition.recId, disposition, closedReason);
            } else {
              await moveRec(pendingDisposition.recId, pendingDisposition.newStatus, { disposition, closedReason });
            }
            setPendingDisposition(null);
          }}
        />
      )}

      <PipelineHeader byColumn={byColumn} total={totalApplications} loaded={loaded} />

      {/* T290 — the route returns `hasMore` per stage and this board dropped it,
          so a stage past 100 rows would truncate in silence and read as the whole
          pipeline. Nowhere near it today — the largest stage holds 46 — but a
          silent cut is the failure this codebase has now found in four separate
          lists, and the cheapest moment to close it is before it bites. */}
      {truncated.length > 0 && (
        <p className="mb-3 rounded-md border border-amber-800/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
          Showing the first 100 of {truncated.join(', ')} — this board does not page yet.
        </p>
      )}

      {/* T424 — name the stages that did not load, instead of blanking the
          board. The rows shown for them are the last ones that arrived, so
          the honest thing is to say they may be out of date and offer the
          one action that helps. */}
      {staleColumns.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-800/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
          <span>
            Could not reload {staleColumns.join(', ')} — showing what was last loaded. Everything else
            is up to date.
          </span>
          <button
            type="button"
            onClick={refresh}
            className="underline decoration-dotted transition hover:text-amber-100"
          >
            Retry
          </button>
        </div>
      )}

      {!loaded ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg border border-slate-800 bg-slate-900/40" />
          ))}
        </div>
      ) : loadErr ? (
        <EmptyState
          icon="⚠"
          title="Could not load your applications"
          body={loadErr}
          actions={[{ label: 'Retry', onClick: refresh, primary: true }]}
        />
      ) : totalApplications === 0 ? (
        <EmptyState
          icon="☰"
          title="No applications tracked yet"
          body="Go to the Dashboard, find a shortlisted job, and hit 'Mark as applied'. It'll show up here ready to track through screening → interview → offer."
          actions={[
            { label: 'Go to Dashboard', to: '/', primary: true },
            { label: 'Edit preferences', to: '/profile' },
          ]}
        />
      ) : (
        <PipelineList
          byColumn={byColumn}
          moveRec={requestMove}
          onRowClick={(rec) => setSelectedRec(rec)}
          onOpenArtifact={openArtifact}
        />
      )}

      {selectedRec && (
        <SideDrawer
          rec={selectedRec}
          // T290c — `globalOverlayOpen` belongs here for the same reason it does
          // on the dashboard: the command palette is mounted at the App root, so
          // this page cannot see it, and both it and the drawer listen for
          // Escape on `window`. Open an application, press ⌘K, change your mind,
          // press Escape — and the drawer went with the palette. The dashboard
          // got this channel and this page did not, which is how a fix ends up
          // covering one of the two surfaces that needed it.
          childModalOpen={Boolean(
            coverLetterRec || tailoredCvRec
            || pendingDisposition || globalOverlayOpen,
          )}
          onClose={() => setSelectedRec(null)}
          onSaveNotes={saveNotes}
          onMove={requestMove}
          onEditOutcome={editOutcome}
          // T474 — through the one writeback, not a second copy of its body.
          // `patchRec`'s own comment calls itself this page's single
          // writeback; this was a hand-rolled duplicate three lines long,
          // which is how "six things that exist exactly once" (T271) quietly
          // becomes five.
          onRecPatched={(patch) => patchRec(selectedRec?.id, patch)}
          onOpenCoverLetter={() => {
            setCoverLetterRec(selectedRec);
          }}
          onOpenInterviewPrep={() => {
            // T422b — opens in the shell now, same as company research.
            openShellArtifact('interview-prep', selectedRec);
          }}
          onOpenCompanyResearch={() => {
            // T422a — one mount, in the shell; the page only names the company.
            openCompanyResearchGlobal(selectedRec.job?.company || null);
          }}
          onOpenTailoredCv={() => {
            setTailoredCvRec(selectedRec);
          }}
        />
      )}
    </div>
  );
}


// ─── Pipeline header — at-a-glance summary of where everything stands ──────
function PipelineHeader({ byColumn, total, loaded }) {
  // T254(3) — the chip row was built strictly from columns, so the single
  // number the operator most needs ("how many of these have gone quiet?") had
  // no aggregate anywhere: twelve silent applications were spread across four
  // stage chips as ordinary counts. Only ACTIVE stages — a rejection is not
  // stale, it is finished. Shares the URL filter state with PipelineList.
  const [, setUrl] = useUrlFilters(APPLICATIONS_FILTER_SPEC);
  const staleCount = ACTIVE_COLUMNS.reduce(
    (n, c) => n + (byColumn[c.key] ?? []).filter((r) => recIsStale(r)).length,
    0,
  );
  const counts = COLUMNS.reduce(
    (acc, c) => {
      acc[c.key] = byColumn[c.key]?.length ?? 0;
      return acc;
    },
    {},
  );
  const activeTotal = ACTIVE_COLUMNS.reduce((n, c) => n + counts[c.key], 0);
  const closedTotal = CLOSED_COLUMNS.reduce((n, c) => n + counts[c.key], 0);

  return (
    <header className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-slate-100">Application tracker</h1>
        {loaded && total > 0 && (
          <p className="text-xs text-slate-500">
            <span className="text-slate-300">{activeTotal}</span> in pipeline ·{' '}
            <span className="text-slate-400">{closedTotal}</span> closed
          </p>
        )}
      </div>
      {!loaded || total === 0 ? (
        <p className="text-sm text-slate-400">
          {total === 0
            ? 'Nothing here yet. Mark a recommendation as Applied on the dashboard and it appears here, then advance it through the stages with “→ Next”.'
            : 'Loading…'}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {ACTIVE_COLUMNS.map((c) => (
            <SummaryChip key={c.key} stageKey={c.key} label={c.label} count={counts[c.key]} accent={c.accent} muted={counts[c.key] === 0} />
          ))}
          {staleCount > 0 && (
            <button
              type="button"
              onClick={() => setUrl({ filterKey: 'stale' })}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-800 px-2 py-0.5 text-amber-300 transition hover:border-amber-600 hover:bg-amber-950/30"
              title={`Show the ${staleCount} application${staleCount === 1 ? '' : 's'} with no reply for ${STALE_DAYS}+ days`}
              aria-label={`Show ${staleCount} application${staleCount === 1 ? '' : 's'} with no reply for ${STALE_DAYS}+ days`}
            >
              <span>⚠ {staleCount}</span>
              <span className="text-[10px] uppercase tracking-wide">stale</span>
            </button>
          )}
          <span className="text-slate-700">|</span>
          {CLOSED_COLUMNS.map((c) => (
            <SummaryChip key={c.key} stageKey={c.key} label={c.label} count={counts[c.key]} accent={c.accent} muted={counts[c.key] === 0} />
          ))}
        </div>
      )}
    </header>
  );
}

function SummaryChip({ stageKey, label, count, accent, muted }) {
  // T082 — Each chip is now a hover-tooltip target. Stage meta comes from
  // the shared STAGES map so the copy can't drift from the StatusPill.
  const stage = STAGES[stageKey];
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        tabIndex={stage ? 0 : -1}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={
          'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 ' +
          (stage ? 'cursor-help ' : '') +
          (muted ? 'border-slate-800 text-slate-600' : 'border-slate-700 text-slate-300')
        }
      >
        <span className={muted ? '' : accent}>{count}</span>
        <span className="text-[10px] uppercase tracking-wide">{label}</span>
      </span>
      {open && stage && (
        <div
          role="tooltip"
          className="absolute left-0 top-full z-30 mt-2 w-64 rounded-lg border border-slate-700 bg-slate-900/95 p-3 text-left text-xs text-slate-200 shadow-xl backdrop-blur"
        >
          <p className="font-semibold text-slate-100">{stage.label}</p>
          <p className="mt-1 leading-snug text-slate-300">{stage.desc}</p>
          <p className="mt-2 text-[10px] uppercase tracking-widest text-slate-500">Next move</p>
          <p className="mt-0.5 leading-snug text-slate-400">{stage.next}</p>
        </div>
      )}
    </span>
  );
}

// ─── List view — per-stage groups, sortable, filterable, wide rows ────────
// Sort + filter live at the parent so the same controls drive both
// pipeline and closed sections. STALE_DAYS_THRESHOLD is the operator's
// "this isn't moving" line — surfaced in the chip + the row warning.
// T223 — re-exported from the shared rule so the page, aiTriage and the digest
// cannot drift apart on what "no activity" means. They already had.
const STALE_DAYS_THRESHOLD = STALE_DAYS;
// T203 — what an application IS, as a row. Deliberately the fields the operator
// would sort or pivot on in a spreadsheet, not a dump of the document: ids,
// embeddings and AI artefacts are noise in Excel.
const CSV_COLUMNS = [
  { label: 'Title', get: (r) => r.job?.title },
  { label: 'Company', get: (r) => r.job?.company },
  { label: 'Stage', get: (r) => r.status },
  { label: 'Applied', get: (r) => (r.appliedAt ? String(r.appliedAt).slice(0, 10) : '') },
  { label: 'Days silent', get: (r) => daysSilent(r) ?? '' },
  { label: 'Score', get: (r) => r.scoreTotal },
  { label: 'City', get: (r) => r.job?.location?.city },
  { label: 'Country', get: (r) => r.job?.location?.country },
  { label: 'Source', get: (r) => r.job?.source },
  { label: 'CV version', get: (r) => r.cvSnapshot?.version ?? '' },
  { label: 'Outcome', get: (r) => r.disposition ?? '' },
  { label: 'Closed reason', get: (r) => r.closedReason ?? '' },
  { label: 'Notes', get: (r) => r.applicationNotes ?? '' },
  { label: 'URL', get: (r) => r.job?.url },
];

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most recent' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'score', label: 'Score (high → low)' },
  { value: 'title', label: 'Title A → Z' },
];
const FILTER_OPTIONS = [
  { value: 'active', label: 'Active only' },
  { value: 'all', label: 'All' },
  { value: 'stale', label: `Stale (${STALE_DAYS_THRESHOLD}+ days no activity)` },
];

function PipelineList({ byColumn, moveRec, onRowClick, onOpenArtifact }) {
  // T137 — sort/show live in the URL (shareable + Back-able). mobileStage stays
  // local: it's transient UI, not a filter.
  const [urlState, setUrl] = useUrlFilters(APPLICATIONS_FILTER_SPEC);
  const { searchQ, sortKey, filterKey, source, company, country, band, cv, reached } = urlState;

  // T203 — the same named views the dashboard has had since T137, on the board
  // where they arguably matter more: "stale in screening", "everything from
  // jobindex", "closed this quarter" are questions the operator re-asks, and
  // rebuilding them meant three controls and a search box every time.
  //
  // Its own namespace, so a dashboard view never appears here — the two pages
  // do not share a filter vocabulary and a view that silently does nothing is
  // worse than no view.
  const VIEW_NS = 'applications';
  const [views, setViews] = useState(() => listViews(VIEW_NS));
  const currentQuery = useMemo(() => serializeFilters(APPLICATIONS_FILTER_SPEC, urlState), [urlState]);
  const activeViewId = views.find((v) => sameQuery(v.query, currentQuery))?.id ?? null;
  const refreshViews = () => setViews(listViews(VIEW_NS));
  const onSaveView = (name) => {
    saveView(VIEW_NS, name, currentQuery);
    refreshViews();
  };
  const onApplyView = (v) => setUrl(parseQuery(APPLICATIONS_FILTER_SPEC, v.query));
  const onDeleteView = (id) => {
    deleteView(VIEW_NS, id);
    refreshViews();
  };
  const onSetDefaultView = (id) => {
    setDefaultView(VIEW_NS, id);
    refreshViews();
  };

  // Cold load only, and only when the URL carries no filters of its own: a
  // shared or drilled link always wins over a saved default. `replace` so the
  // Back button does not land on a view the operator never chose.
  // The router's params, not window.location: under any router but Browser the
  // two disagree, and a test could not tell the guard from its absence.
  const [rawParams] = useSearchParams();
  // Absent means default. An operator clicking "Active only" writes nothing to
  // the URL (setUrl omits defaults), so a `show=` param can only have come from
  // a hand-built or analytics-generated link — i.e. someone asked for it.
  const showIsExplicit = rawParams.get('show') !== null;
  const mountedQuery = useRef([...rawParams].length > 0);
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    appliedDefaultRef.current = true;
    if (mountedQuery.current) return;
    const def = getDefaultView(VIEW_NS);
    if (def) setUrl(parseQuery(APPLICATIONS_FILTER_SPEC, def.query), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const drill = { source, company, country, band, cv, reached };
  const activeDrill = DRILL_KEYS.filter((k) => drill[k]);
  const [mobileStage, setMobileStage] = useState('applied'); // T122 — which stage the mobile tabs show

  const compareRecs = (a, b) => {
    switch (sortKey) {
      case 'recent':
        return new Date(b.updatedAt ?? b.appliedAt ?? 0) - new Date(a.updatedAt ?? a.appliedAt ?? 0);
      case 'oldest':
        return new Date(a.updatedAt ?? a.appliedAt ?? 0) - new Date(b.updatedAt ?? b.appliedAt ?? 0);
      case 'score':
        return (b.scoreTotal ?? 0) - (a.scoreTotal ?? 0);
      case 'title':
        return String(a.job?.title ?? '').localeCompare(String(b.job?.title ?? ''));
      default:
        return 0;
    }
  };

  // Drill-down match (from an analytics dimension click). When any drill is
  // active it narrows by job attribute AND relaxes the active/stale show filter
  // to 'all', so a drilled rejected/withdrawn app isn't hidden.
  const matchesDrill = (rec) => {
    // Normalize with the same 'unknown' sentinel the analytics rollup uses
    // (dimensionRollup: key || 'unknown'), so a drill into the "unknown" bucket
    // faithfully selects the attribute-less recs instead of dead-ending.
    if (source && (rec.job?.source || 'unknown') !== source) return false;
    if (company && (rec.job?.company || 'unknown') !== company) return false;
    if (country && (rec.job?.location?.country || 'unknown') !== country) return false;
    // T331 — the band is DERIVED, so it has to be derived the same way the bar
    // that linked here derived it. `bandOf` is the shared rule; a local copy of
    // the thresholds is how a bar and the list it opens start disagreeing about
    // what "40+" contains. A never-scored row has band null, not '0–9'.
    if (band && (bandOf(rec.scoreTotal) ?? 'unknown') !== band) return false;
    // T334 — the snapshot is frozen at apply-time (T024), so this is the CV the
    // company actually saw, not whichever is active now.
    if (cv && String(rec.cvSnapshot?.version ?? 'unknown') !== cv) return false;
    // T340 — via the shared rule, so the bar and this list cannot disagree
    // about what "reached interview" contains.
    if (reached && !reachedStage(rec, reached)) return false;
    return true;
  };

  // T231 — same haystack the dashboard searches, minus the fields this page
  // does not carry, plus the two it does: the operator's own notes and the
  // close-out reason. Those are where he writes what he will later search for.
  const q = searchQ.trim().toLowerCase();
  const matchesSearch = (rec) => {
    if (!q) return true;
    return [
      rec.job?.title,
      rec.job?.company,
      rec.job?.location?.city,
      rec.job?.location?.country,
      rec.job?.source,
      rec.applicationNotes,
      rec.closedReason,
      ...(rec.job?.extracted?.skills ?? []),
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  };

  const passesFilter = (rec) => {
    if (!matchesSearch(rec)) return false;
    if (!matchesDrill(rec)) return false;
    // A drill relaxes the DEFAULT show-filter, so a drilled rejected/withdrawn
    // app isn't hidden by "Active only" that the operator never chose. T261: it
    // must NOT relax a filter that was explicitly asked for — analytics now
    // links here with ?source=X&show=stale to mean the intersection, and
    // returning every application of that source would answer a different
    // question than the one the link was labelled with.
    if (activeDrill.length > 0 && filterKey === 'active') return true;
    // T203 — and a typed search is a drill by another name. Searching "norlys"
    // on the default board answered "Nothing matches" for a company that had
    // rejected him: the row matched, then `active` dropped it for being closed.
    // T231 deliberately put `closedReason` in the haystack, a field that only
    // exists on terminal rows, so that half of the search was dead on arrival.
    // Same T261 rule as the drill: relax only the DEFAULT. An explicit `show=`
    // in the URL was asked for and is honoured.
    if (q && !showIsExplicit && filterKey === 'active') return true;
    if (filterKey === 'all') return true;
    const isActive = ACTIVE_COLUMNS.some((c) => c.key === rec.status);
    if (filterKey === 'active') return isActive;
    // T223 — the same shared rule the row badge uses. This branch had its own
    // copy, reading `updatedAt`, so the filter and the badge could disagree
    // about the very same row.
    if (filterKey === 'stale') return recIsStale(rec);
    return true;
  };

  const filteredByColumn = useMemo(() => {
    const out = {};
    for (const col of COLUMNS) {
      out[col.key] = (byColumn[col.key] ?? []).filter(passesFilter).slice().sort(compareRecs);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byColumn, searchQ, sortKey, filterKey, showIsExplicit, source, company, country, band, cv, reached]);

  const activeShown = ACTIVE_COLUMNS.reduce((n, c) => n + filteredByColumn[c.key].length, 0);
  const closedShown = CLOSED_COLUMNS.reduce((n, c) => n + filteredByColumn[c.key].length, 0);
  // T203 — the denominator. Everything loaded, before any filter: without it
  // "6 applications" is unreadable, because 6 of 6 and 6 of 91 look identical.
  const loadedTotal = COLUMNS.reduce((n, c) => n + (byColumn[c.key]?.length ?? 0), 0);
  const filtersDirty = Boolean(searchQ) || filterKey !== 'active' || activeDrill.length > 0;

  // Exports exactly WHAT IS ON SCREEN, in the order it is shown. Exporting the
  // whole board regardless of the filters would be a different, surprising
  // dataset — the operator filtered for a reason, and the button sits inside
  // the filter bar.
  const exportCsv = () => {
    const rows = COLUMNS.flatMap((c) => filteredByColumn[c.key] ?? []);
    downloadCsv(csvFilename('applications'), toCsv(CSV_COLUMNS, rows));
  };

  return (
    <div className="space-y-6">
      <FilterBar
        shown={activeShown + closedShown}
        total={loadedTotal}
        noun="applications"
        dirty={filtersDirty}
        onReset={() => setUrl({ searchQ: '', filterKey: 'active', source: '', company: '', country: '' })}
      >
        {/* T231 — `replace: true` so typing does not stack a history entry per
            keystroke; the dashboard's search does the same. */}
        <input
          type="search"
          value={searchQ}
          onChange={(e) => setUrl({ searchQ: e.target.value }, { replace: true })}
          placeholder="Search title / company / notes…"
          aria-label="Search applications"
          className="min-w-[190px] flex-1 rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
        />
        <SegmentedControl label="Show" value={filterKey} options={FILTER_OPTIONS} onChange={(v) => setUrl({ filterKey: v })} />
        <span className="text-slate-700">|</span>
        <SegmentedControl label="Sort" value={sortKey} options={SORT_OPTIONS} onChange={(v) => setUrl({ sortKey: v })} />
        <button
          type="button"
          onClick={exportCsv}
          disabled={activeShown + closedShown === 0}
          title="Download the applications shown below, with the current filters applied"
          className="ml-auto rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-400 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-40"
        >
          ↓ CSV <span className="text-slate-600">({activeShown + closedShown})</span>
        </button>
        {activeDrill.length > 0 && (
          <span className="contents" role="status" aria-live="polite">
            <span className="text-slate-700">|</span>
            {activeDrill.map((k) => (
              <span key={k} className="inline-flex items-center gap-1 rounded-full border border-brand-700 bg-brand-600/15 px-2 py-0.5 text-brand-400">
                <span className="text-[10px] uppercase tracking-wide text-brand-500">{k}</span>
                <span className="font-medium">{drill[k]}</span>
                <button
                  type="button"
                  onClick={() => setUrl({ [k]: '' })}
                  aria-label={`Clear ${k} filter`}
                  className="ml-0.5 text-brand-400 hover:text-brand-500"
                >
                  ✕
                </button>
              </span>
            ))}
          </span>
        )}
      </FilterBar>

      <SavedViewsBar
        views={views}
        activeViewId={activeViewId}
        hasActiveFilters={Boolean(searchQ || filterKey !== 'active' || sortKey !== 'recent' || activeDrill.length > 0)}
        onApply={onApplyView}
        onSave={onSaveView}
        onDelete={onDeleteView}
        onSetDefault={onSetDefaultView}
      />

      {/* T122 — desktop: full stacked Pipeline + Closed sections */}
      <div className="hidden space-y-6 md:block">
      <section>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-[10px] uppercase tracking-widest text-brand-500">
            Pipeline {activeShown > 0 && `(${activeShown})`}
          </h2>
          <span className="h-px flex-1 bg-slate-800" />
        </div>
        {activeShown === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-800 bg-slate-900/20 p-4 text-xs text-slate-500">
            {q
              ? `Nothing matches “${searchQ}”${closedShown > 0 ? ' in the pipeline — see Closed below.' : '.'}`
              : activeDrill.length > 0
                ? `No in-pipeline applications for this ${activeDrill.join(' / ')}${closedShown > 0 ? ' — see Closed below.' : '.'}`
                : 'No active applications match the current filter.'}
            {/* T256(10) — dead end otherwise: the message names a filter but
                the control that set it is a screen-height away at the top. */}
            {q && (
              <button
                type="button"
                onClick={() => setUrl({ searchQ: '' })}
                className="ml-2 underline decoration-dotted hover:text-brand-400"
              >
                Clear search
              </button>
            )}
            {activeDrill.length === 0 && filterKey !== 'all' && (
              <button
                type="button"
                onClick={() => setUrl({ filterKey: 'all' })}
                className="ml-2 underline decoration-dotted hover:text-brand-400"
              >
                Show all
              </button>
            )}
          </p>
        ) : (
          <div className="space-y-4">
            {ACTIVE_COLUMNS.map((col) => (
              <StageGroup
                key={col.key}
                col={col}
                rows={filteredByColumn[col.key]}
                moveRec={moveRec}
                onRowClick={onRowClick}
                onOpenArtifact={onOpenArtifact}
              />
            ))}
          </div>
        )}
      </section>

      {closedShown > 0 && (
        <section>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-[10px] uppercase tracking-widest text-slate-500">Closed ({closedShown})</h2>
            <span className="h-px flex-1 bg-slate-800" />
          </div>
          <div className="space-y-4">
            {CLOSED_COLUMNS.map((col) => (
              <StageGroup
                key={col.key}
                col={col}
                rows={filteredByColumn[col.key]}
                moveRec={moveRec}
                onRowClick={onRowClick}
                onOpenArtifact={onOpenArtifact}
                muted
              />
            ))}
          </div>
        </section>
      )}
      </div>

      {/* T122 — mobile: one stage at a time via a horizontally-scrollable tab strip */}
      <div className="space-y-3 md:hidden">
        <div className="flex gap-1.5 overflow-x-auto">
          {COLUMNS.map((col) => {
            const n = filteredByColumn[col.key].length;
            const active = mobileStage === col.key;
            return (
              <button
                key={col.key}
                type="button"
                onClick={() => setMobileStage(col.key)}
                aria-pressed={active}
                className={
                  'shrink-0 rounded-full border px-3 py-1.5 text-xs ' +
                  (active
                    ? 'border-brand-500 bg-brand-600/20 text-brand-400'
                    : 'border-slate-700 text-slate-400 hover:border-brand-600')
                }
              >
                {col.label}
                {n > 0 && <span className="ml-1 font-mono text-[10px]">{n}</span>}
              </button>
            );
          })}
        </div>
        {filteredByColumn[mobileStage].length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-800 bg-slate-900/20 p-4 text-xs text-slate-500">
            No applications in this stage.
          </p>
        ) : (
          <StageGroup
            col={COLUMNS.find((c) => c.key === mobileStage)}
            rows={filteredByColumn[mobileStage]}
            moveRec={moveRec}
            onRowClick={onRowClick}
            onOpenArtifact={onOpenArtifact}
            muted={CLOSED_COLUMNS.some((c) => c.key === mobileStage)}
          />
        )}
      </div>
    </div>
  );
}


function StageGroup({ col, rows, moveRec, onRowClick, onOpenArtifact, muted = false }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 px-1">
        <span
          className={
            'rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
            col.tone +
            ' ' +
            (col.accent ?? 'text-slate-300')
          }
        >
          {col.label}
        </span>
        <span className="text-[10px] text-slate-500">{rows.length}</span>
      </div>
      <div className="space-y-2">
        {rows.map((rec) => (
          <PositionRow
            key={rec.id}
            rec={rec}
            moveRec={moveRec}
            onClick={() => onRowClick(rec)}
            onOpenArtifact={onOpenArtifact}
            muted={muted}
          />
        ))}
      </div>
    </div>
  );
}

function PositionRow({ rec, moveRec, onClick, onOpenArtifact, muted = false }) {
  const job = rec.job;
  if (!job) return null;
  const location = [job.location?.city, job.location?.country].filter(Boolean).join(', ');
  // T223 — measured from the last STATUS TRANSITION, not from `updatedAt`.
  // `updatedAt` is "last write of any kind", so a rescore, a semantic pass or a
  // relevance sweep silently reset it: on 2026-09-03 this filter reported zero
  // stale applications while twelve had been silent for a fortnight.
  const daysSinceUpdate = daysSilent(rec);
  const isStale = recIsStale(rec);

  const nextStage = nextActiveStage(rec.status);
  const notesPreview = (rec.applicationNotes ?? '').split(/\r?\n/)[0]?.trim() ?? '';

  return (
    <article
      className={
        'group rounded-lg border bg-slate-950/40 transition hover:border-brand-700 ' +
        (isStale ? 'border-amber-800' : 'border-slate-800') +
        (muted ? ' opacity-80' : '')
      }
    >
      <div className="flex flex-col gap-3 p-3 md:flex-row md:items-start md:gap-4">
        {/* LEFT — title + company + location + notes preview */}
        <button
          type="button"
          onClick={onClick}
          className="flex-1 min-w-0 text-left"
          aria-label={`Open ${job.title} at ${job.company}`}
        >
          <div className="flex items-start gap-2">
            <h3 className="min-w-0 font-semibold text-slate-100 group-hover:text-brand-400">
              {job.title}
            </h3>
            <ScoreBadge
              score={rec.scoreTotal}
              breakdown={rec.scoreBreakdown}
              reasons={rec.reasons}
              risks={rec.risks}
              missingSkills={rec.missingSkills}
              variant="compact"
            />
          </div>
          <p className="mt-0.5 truncate text-sm text-slate-400">
            {job.company}
            {location && <span className="text-slate-600"> · {location}</span>}
            {job.source && <span className="ml-2 font-mono text-[10px] text-slate-700">{job.source}</span>}
          </p>
          {notesPreview && (
            <p className="mt-1 truncate text-xs text-slate-500" title={rec.applicationNotes}>
              📝 {notesPreview}
            </p>
          )}
          {/* Phase 4 — surface the terminal outcome on the card so an accepted
              offer (or a tagged rejection) reads without opening the drawer; a
              muted "Outcome?" nudge when a closed/offer card has none yet. */}
          {statusHasDisposition(rec.status) && (
            <span
              className={
                'mt-1.5 inline-block rounded-full border px-2 py-0.5 text-[10px] ' +
                (rec.disposition === 'offer_accepted'
                  ? 'border-emerald-700 text-emerald-300'
                  : rec.disposition
                    ? 'border-slate-700 text-slate-300'
                    : 'border-amber-800 text-amber-300')
              }
            >
              {rec.disposition ? dispositionLabel(rec.disposition) : 'Outcome?'}
            </span>
          )}
        </button>

        {/* MIDDLE — applied + staleness meta */}
        <div className="flex flex-row items-center gap-3 md:flex-col md:items-end md:gap-0.5 md:text-right">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Applied</div>
          <div className="text-xs text-slate-300">{rec.appliedAt ? timeAgo(rec.appliedAt) : '—'}</div>
          {/* T404 — he applied to Factbird on 18 August and again on 29
              September, and the tracker had no way to say so: it assumes one
              application per job, so the row read as six weeks of silence
              with a confirmation from that afternoon on it. */}
          {rec.reappliedAt && (
            <div className="text-[11px] text-brand-400" title={`Applied again on ${formatDate(rec.reappliedAt)}`}>
              applied again {timeAgo(rec.reappliedAt)}
            </div>
          )}
          {daysSinceUpdate !== null && daysSinceUpdate !== 0 && (
            <div className={'text-xs ' + (isStale ? 'text-amber-400' : 'text-slate-400')}>
              {isStale ? `⚠ ${daysSinceUpdate}d no activity` : `${daysSinceUpdate}d since update`}
            </div>
          )}
        </div>

        {/* RIGHT — asset markers + advance button */}
        <div className="flex shrink-0 flex-row items-center gap-2 md:flex-col md:items-end md:gap-1.5">
          <div className="flex items-center gap-1.5 text-xs">
            {/* T134 — artifact markers: the emoji is decorative; the ready/not
                state is announced via sr-only (was color + title only).
                T422v — and they open the thing they describe. They used to
                report state and refuse to act on it, so producing a missing
                cover letter meant opening the drawer and scrolling to the
                buttons — three interactions to reach the most-used tools in
                the product. `stopPropagation` because the row itself opens
                the drawer. */}
            {[
              { kind: 'cover-letter', glyph: '✍', ready: Boolean(rec.coverLetter), noun: 'Cover letter' },
              { kind: 'tailored-cv', glyph: '📑', ready: Boolean(rec.tailoredCv?.summary), noun: 'Tailored CV' },
              { kind: 'interview-prep', glyph: '🎙', ready: rec.interviewPrep?.questions?.length > 0, noun: 'Interview prep' },
            ].map((a) => (
              <button
                key={a.kind}
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onOpenArtifact?.(rec, a.kind);
                }}
                title={a.ready ? `${a.noun} ready — open it` : `No ${a.noun.toLowerCase()} — write one`}
                className={
                  'rounded transition hover:text-brand-400 ' +
                  (a.ready ? 'text-emerald-400' : 'text-slate-700')
                }
              >
                <Emoji label={`${a.noun} ${a.ready ? 'ready' : 'missing'}. Open. `}>{a.glyph}</Emoji>
              </button>
            ))}
            {rec.cvSnapshot?.version && (
              <span className="font-mono text-[10px] text-emerald-400" title={`CV version sent with this application${rec.cvSnapshot.filename ? `: ${rec.cvSnapshot.filename}` : ''}`}>
                v{rec.cvSnapshot.version}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {nextStage && (
              <button
                type="button"
                onClick={() => moveRec(rec.id, nextStage.key)}
                className="rounded border border-brand-700 px-2 py-0.5 text-xs text-brand-400 hover:bg-brand-600/10"
                title={`Advance to ${nextStage.label}`}
              >
                → {nextStage.label}
              </button>
            )}
            {/* T254(4) — rejection is the most common outcome and had no direct
                path: open drawer → Stage select → confirm → close, four steps
                for the thing that happens most. moveRec here is requestMove,
                which already routes a live→terminal move into the disposition
                modal, so the confirm is unchanged — only the walk to it is. */}
            {!TERMINAL_KEYS.has(rec.status) && (
              <button
                type="button"
                onClick={() => moveRec(rec.id, 'rejected')}
                className="rounded border border-slate-800 px-1.5 py-0.5 text-xs text-slate-500 transition hover:border-red-700 hover:text-red-400"
                title="Close as rejected"
                aria-label={`Close ${job.title} as rejected`}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function nextActiveStage(currentKey) {
  const idx = ACTIVE_COLUMNS.findIndex((c) => c.key === currentKey);
  if (idx < 0) return null;
  return ACTIVE_COLUMNS[idx + 1] ?? null;
}

// Format "today" / "1d ago" / "5d ago" / "3w ago" / "2mo ago" — humans
// scan a list of dates faster as relative deltas. Restored after the
// Kanban-→-list refactor removed it along with the old Card block.
function SideDrawer({ rec, onClose, childModalOpen, onSaveNotes, onMove, onEditOutcome, onRecPatched, onOpenCoverLetter, onOpenInterviewPrep, onOpenCompanyResearch, onOpenTailoredCv }) {
  const job = rec.job;
  const [notes, setNotes] = useState(rec.applicationNotes ?? '');
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = notes !== (rec.applicationNotes ?? '');

  useEffect(() => {
    setNotes(rec.applicationNotes ?? '');
  }, [rec.id, rec.applicationNotes]);

  const save = async () => {
    setSaving(true);
    await onSaveNotes(rec.id, notes);
    setSaving(false);
  };

  // T423 — closing with unsaved notes asks first.
  //
  // `dirty` was computed and used for exactly one thing: greying out the
  // Save button. Every way OUT of the drawer — the ✕, Escape, a click on
  // the scrim — called `onClose` straight through, so notes typed and not
  // saved were gone without a word. These are phone-screen times and
  // recruiter names, typed during a call; there is nowhere else they exist.
  //
  // The pattern is already in this codebase: CoverLetterModal's
  // `attemptClose` + ConfirmDialog (T111), including the Escape arbitration
  // — while the confirm is up, one Escape closes the confirm, not the
  // drawer underneath it.
  const attemptClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose?.();
  };


  return (
    // T248 — was a hand-rolled scrim + <aside>: no role, no aria-modal, no focus
    // trap and no focus restore, so Tab left the drawer and closing it dropped
    // focus to <body>. ui/Drawer (T131) was written for exactly this shape —
    // its own doc names "SideDrawer's w-full max-w-md" — and carries the trap,
    // the gated Escape and the restore. tone="drawer" keeps z-40 so the modals
    // this drawer opens still render above it.
    // T224 — Escape belongs to whatever is on top. This drawer kept its own
    // unconditional listener as well as the primitive's, so dismissing one of
    // the five modals it opens closed the drawer underneath it too.
    <>
    <Drawer
      onClose={attemptClose}
      tone="drawer"
      side="right"
      panelClassName="w-full max-w-md"
      labelledById="application-drawer-title"
      escapeClosesOnTop={!childModalOpen && !confirmClose}
    >
      <>
        <header className="flex items-start justify-between border-b border-slate-800 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-brand-500">Application</p>
            <h2 id="application-drawer-title" className="mt-0.5 truncate text-base font-semibold text-slate-100">{job?.title}</h2>
            <p className="truncate text-xs text-slate-400">@ {job?.company}</p>
            <p className="mt-1 text-xs text-slate-500">
              {[job?.location?.city, job?.location?.country].filter(Boolean).join(', ')}
              {rec.appliedAt && ` · Applied ${formatDate(rec.appliedAt)}`}
              {rec.cvSnapshot?.version && (
                <span className="ml-1 text-emerald-400">· CV v{rec.cvSnapshot.version}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-400">Stage</label>
            <select
              value={rec.status}
              onChange={(e) => onMove(rec.id, e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
            >
              {COLUMNS.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
              {/* Un-apply: drop the rec back out of the tracker onto the
                  dashboard shortlist (e.g. an accidental "Mark as applied").
                  status 'shortlisted' isn't a column, so it leaves the board. */}
              <option value="shortlisted">↩ Back to shortlist (un-apply)</option>
            </select>
            <p className="mt-1 text-xs text-slate-500">
              The “→ Next” button on the card does the same thing one stage at a time. Pick “Back to shortlist” to undo an accidental apply.
            </p>
          </div>

          {/* Phase 4 — terminal outcome (offer/rejected/withdrawn). The only way
              to record offer_accepted vs offer_declined, since you don't drag to
              "accepted" — the card stays in Offer. */}
          {statusHasDisposition(rec.status) && (
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400">Outcome</label>
              <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2">
                <div className="min-w-0">
                  {rec.disposition ? (
                    <p className="truncate text-sm text-slate-200">{dispositionLabel(rec.disposition)}</p>
                  ) : (
                    <p className="text-sm text-slate-500">Not recorded yet</p>
                  )}
                  {rec.closedReason && (
                    <p className="mt-0.5 truncate text-xs text-slate-500" title={rec.closedReason}>{rec.closedReason}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onEditOutcome(rec)}
                  className="shrink-0 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 transition hover:border-brand-600 hover:text-brand-400"
                >
                  {rec.disposition ? 'Edit' : 'Set outcome'}
                </button>
              </div>
            </div>
          )}

          {/* T128 — surface staleness inside the drawer too (the list view
              already flags it). T254: this was the last copy of the rule that
              predates T223 — it read `updatedAt || appliedAt`, and updatedAt is
              stamped by anything that touches the row (an AI artifact, a note),
              so writing a note reset the silence counter to zero. That is the
              exact anti-pattern shared/src/applications.js was written to end.
              recIsStale also scopes "active" the same way everywhere. */}
          {recIsStale(rec) && (
            <p className="rounded-md border border-amber-800 bg-amber-950/15 px-2.5 py-1.5 text-xs text-amber-300">
              ⚠ {daysSilent(rec)}d with no reply — consider a follow-up.
            </p>
          )}

          {/* T128 — stage-transition timeline */}
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Stage history</label>
            <div className="mt-2">
              <StageTimeline history={rec.statusHistory} createdAt={rec.createdAt} />
            </div>
          </div>

          {/* T358 — the mail behind the timeline above. Every row up there is a
              move the operator made by hand; this is what prompted them. */}
          <MailThread recommendationId={rec.id} />

          {/* T088 — Pipeline coach panels. Render conditionally based on
              stage (stale → follow-up; interview → retro; offer → negotiation). */}
          <FollowUpPanel rec={rec} onUpdated={onRecPatched} />
          <InterviewRetroPanel rec={rec} onUpdated={onRecPatched} />
          <NegotiationPrepPanel rec={rec} onUpdated={onRecPatched} />

          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-400">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={10}
              placeholder='e.g. "Recruiter: Anna Hansen (LinkedIn). Phone screen scheduled for Thu 14:00. Asked about TF Cloud experience."'
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
            />
            <p className="mt-1 text-xs text-slate-500">
              Freeform scratch pad. Persists per application.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
            >
              {saving ? 'saving…' : 'Save notes'}
            </button>
            <button
              type="button"
              onClick={onOpenCoverLetter}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              {rec.coverLetter ? '✍ Cover letter ✓' : '✍ Cover letter'}
            </button>
            <button
              type="button"
              onClick={onOpenInterviewPrep}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              {rec.interviewPrep?.questions?.length ? '🎙 Interview prep ✓' : '🎙 Interview prep'}
            </button>
            <button
              type="button"
              onClick={onOpenCompanyResearch}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              🏢 Company research
            </button>
            <button
              type="button"
              onClick={onOpenTailoredCv}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              {rec.tailoredCv?.summary ? '📑 Tailored CV ✓' : '📑 Tailored CV'}
            </button>
            {job?.url && (
              <a
                href={job.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
              >
                ↗ Job posting
              </a>
            )}
          </div>
        </div>
      </>
    </Drawer>

      {/* T423 — a sibling of <Drawer>, not a child, so it renders above it
          (zLayers: drawer z-40, dialog z-60), exactly as the artefact
          modals' confirms sit above their Modal.
          Two buttons, not three: ConfirmDialog offers confirm/cancel and I
          am not widening a shared primitive for one caller. "Keep writing"
          returns him to the Save button two lines below the textarea, so
          nothing is lost either way — which is the whole point. Same
          wording shape as CoverLetterModal's (T111) so the two read alike. */}
      {confirmClose && (
        <ConfirmDialog
          title="Discard unsaved notes?"
          message="You've typed notes on this application and haven't saved them. Closing now loses them."
          confirmLabel="Discard & close"
          cancelLabel="Keep writing"
          onCancel={() => setConfirmClose(false)}
          onConfirm={() => {
            setConfirmClose(false);
            onClose?.();
          }}
        />
      )}
    </>
  );
}

// T128 — vertical stage-transition timeline for the side drawer. Anchors on the
// rec's createdAt ("Added to hunter") + each recorded {from,to,at}. Reuses the
// shared STAGES labels + timeAgo (absolute date in the title), per T123 policy.
function StageTimeline({ history, createdAt }) {
  const entries = [];
  if (createdAt) entries.push({ to: 'new', at: createdAt, synthetic: true });
  for (const h of history ?? []) entries.push(h);
  if (entries.length <= 1) {
    return (
      <p className="text-xs text-slate-500">No stage changes yet — transitions will be logged here.</p>
    );
  }
  return (
    <ol className="relative space-y-3 border-l border-slate-800 pl-4">
      {entries.map((e, i) => {
        const meta = STAGES[e.to];
        return (
          <li key={i} className="relative">
            <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border border-slate-700 bg-slate-900" />
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-medium text-slate-200">
                {e.synthetic ? (
                  'Added to hunter'
                ) : (
                  <>
                    Moved to <span className="text-brand-400">{meta?.label ?? e.to}</span>
                  </>
                )}
              </span>
              <span className="shrink-0 text-[10px] text-slate-500" title={formatDateTime(e.at)}>
                {timeAgo(e.at)}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
